<template>
    <b-card title="Versions">
        <b-alert v-if="versions.length === 0" variant="secondary" show>
            <div class="alert-body">
                No versions have been created yet. Use the configuration editor to create the first immutable revision.
            </div>
        </b-alert>

        <template v-else>
            <b-table
                hover
                responsive
                show-empty
                class="manifest-versions__table"
                :items="orderedVersions"
                :fields="fields"
                :tbody-tr-class="rowClass"
                @row-clicked="selectVersion"
            >
                <template #cell(revision)="data">
                    <strong>Revision {{ data.item.revision }}</strong>
                    <b-badge
                        v-if="data.item.id === activeVersionId"
                        class="ml-50"
                        variant="success"
                    >
                        Active
                    </b-badge>
                </template>

                <template #cell(validation)="data">
                    <span v-if="data.item.validationErrorCount" class="text-danger">
                        {{ data.item.validationErrorCount }} errors
                    </span>
                    <span v-else class="text-success">Valid</span>
                    <span v-if="data.item.validationWarningCount" class="ml-50 text-warning">
                        {{ data.item.validationWarningCount }} warnings
                    </span>
                </template>

                <template #cell(compilation)="data">
                    <b-badge :variant="data.item.compilationFingerprint ? 'light-success' : 'light-secondary'">
                        {{ data.item.compilationFingerprint ? 'Compiled' : 'Not compiled' }}
                    </b-badge>
                </template>

                <template #cell(createdAt)="data">
                    {{ formatDate(data.item.createdAt) }}
                </template>

                <template #cell(actions)="data">
                    <b-button
                        size="sm"
                        variant="flat-primary"
                        @click.stop="selectVersion(data.item)"
                    >
                        View
                    </b-button>
                </template>
            </b-table>

            <section v-if="selectedVersionId" class="manifest-version-detail">
                <div class="manifest-version-detail__header">
                    <div>
                        <div class="d-flex flex-wrap align-items-center mb-25">
                            <h4 class="mb-0 mr-75">Revision {{ selectedVersion.revision }}</h4>
                            <b-badge
                                v-if="isSelectedVersionActive"
                                variant="success"
                                class="mr-50"
                            >
                                Active
                            </b-badge>
                            <b-badge
                                :variant="selectedVersion.activatable ? 'light-success' : 'light-danger'"
                            >
                                {{ selectedVersion.activatable ? 'Activatable' : 'Not activatable' }}
                            </b-badge>
                        </div>
                        <div class="text-muted font-small-2">
                            Created by {{ selectedVersion.createdBy || '—' }}
                            · {{ formatDate(selectedVersion.createdAt) }}
                        </div>
                    </div>

                    <div class="d-flex align-items-center">
                        <b-button
                            v-if="!isSelectedVersionActive"
                            variant="primary"
                            class="mr-75"
                            :disabled="!canActivateSelectedVersion"
                            @click="activateSelectedVersion"
                        >
                            <b-spinner v-if="activating" small class="mr-50" />
                            {{ activating ? 'Activating' : 'Activate' }}
                        </b-button>
                        <b-button
                            size="sm"
                            variant="flat-secondary"
                            class="btn-icon"
                            title="Close version details"
                            aria-label="Close version details"
                            @click="closeDetails"
                        >
                            <feather-icon icon="XIcon" size="18" />
                        </b-button>
                    </div>
                </div>

                <div v-if="loadingSelectedVersion" class="manifest-version-detail__loading">
                    <b-spinner small class="mr-75" />
                    Loading revision details…
                </div>

                <template v-else-if="hasSelectedVersionDetails">
                    <div class="manifest-version-detail__section">
                        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
                            <div>
                                <h5 class="mb-25">Validation report</h5>
                                <div class="text-muted font-small-2">
                                    Backend validation result for this immutable definition.
                                </div>
                            </div>
                            <div>
                                <b-badge
                                    v-if="selectedVersion.validationErrorCount"
                                    variant="light-danger"
                                    class="mr-50"
                                >
                                    {{ selectedVersion.validationErrorCount }} errors
                                </b-badge>
                                <b-badge
                                    v-if="selectedVersion.validationWarningCount"
                                    variant="light-warning"
                                >
                                    {{ selectedVersion.validationWarningCount }} warnings
                                </b-badge>
                            </div>
                        </div>

                        <b-alert v-if="!validationGroups.length" variant="success" show class="mb-0">
                            <div class="alert-body d-flex align-items-center">
                                <feather-icon icon="CheckCircleIcon" size="18" class="mr-75" />
                                This revision passed backend validation.
                            </div>
                        </b-alert>

                        <div v-else class="manifest-version-detail__validation-groups">
                            <article
                                v-for="group in validationGroups"
                                :key="group.path"
                                class="manifest-version-detail__validation-group"
                            >
                                <code class="manifest-version-detail__path">{{ group.path }}</code>
                                <div
                                    v-for="issue in group.issues"
                                    :key="`${issue.severity}:${issue.code}:${issue.message}`"
                                    class="manifest-version-detail__issue"
                                >
                                    <b-badge
                                        :variant="issue.severity === 'warning' ? 'light-warning' : 'light-danger'"
                                    >
                                        {{ issue.severity }}
                                    </b-badge>
                                    <div>
                                        <div>{{ issue.message }}</div>
                                        <code>{{ issue.code }}</code>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </div>

                    <div class="manifest-version-detail__section">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h5 class="mb-25">Immutable definition</h5>
                                <div class="text-muted font-small-2">
                                    Definition hash: <code>{{ selectedVersion.definitionHash || '—' }}</code>
                                </div>
                            </div>
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                @click="showDefinition = !showDefinition"
                            >
                                {{ showDefinition ? 'Hide JSON' : 'Show JSON' }}
                            </b-button>
                        </div>
                        <pre v-if="showDefinition" class="manifest-version-detail__json mb-0">{{ formattedDefinition }}</pre>
                    </div>
                </template>
            </section>
        </template>
    </b-card>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BSpinner,
    BTable,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'

export default {
    name: 'ManifestVersions',
    components: {
        BAlert,
        BBadge,
        BButton,
        BCard,
        BSpinner,
        BTable,
    },
    mixins: [backendResponse],
    props: {
        manifestId: {
            type: String,
            default: null,
        },
        versions: {
            type: Array,
            default: () => [],
        },
        activeVersionId: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activating: false,
            detailsById: {},
            fields: [
                {key: 'revision', label: 'Revision'},
                {key: 'validation', label: 'Validation'},
                {key: 'compilation', label: 'Compilation'},
                {key: 'createdBy', label: 'Created by'},
                {key: 'createdAt', label: 'Created at'},
                {key: 'actions', label: '', thClass: 'text-right', tdClass: 'text-right'},
            ],
            loadingVersionId: null,
            requestId: 0,
            selectedVersionId: null,
            showDefinition: false,
        }
    },
    computed: {
        orderedVersions() {
            return [...this.versions].sort((left, right) => right.revision - left.revision)
        },
        selectedVersionSummary() {
            return this.versions.find(version => version.id === this.selectedVersionId) || {}
        },
        selectedVersion() {
            return this.detailsById[this.selectedVersionId] || this.selectedVersionSummary
        },
        loadingSelectedVersion() {
            return this.loadingVersionId === this.selectedVersionId
        },
        hasSelectedVersionDetails() {
            return Boolean(
                this.selectedVersion
                && this.selectedVersion.definition
                && this.selectedVersion.validationReport
            )
        },
        isSelectedVersionActive() {
            return this.selectedVersionId === this.activeVersionId
        },
        canActivateSelectedVersion() {
            return Boolean(
                this.selectedVersionId
                && this.selectedVersion.activatable
                && !this.isSelectedVersionActive
                && !this.disabled
                && !this.activating
                && !this.loadingSelectedVersion
            )
        },
        validationIssues() {
            return this.hasSelectedVersionDetails
                && Array.isArray(this.selectedVersion.validationReport.issues)
                ? this.selectedVersion.validationReport.issues
                : []
        },
        validationGroups() {
            const groups = this.validationIssues.reduce((result, issue) => {
                const path = issue.path || '/'
                if (!result[path]) {
                    result[path] = []
                }
                result[path].push(issue)
                return result
            }, {})

            return Object.entries(groups).map(([path, issues]) => ({
                path,
                issues: [...issues].sort((left, right) => (
                    left.severity === right.severity
                        ? 0
                        : left.severity === 'error' ? -1 : 1
                )),
            }))
        },
        formattedDefinition() {
            return this.hasSelectedVersionDetails
                ? JSON.stringify(this.selectedVersion.definition, null, 2)
                : ''
        },
    },
    watch: {
        versions(versions) {
            if (
                this.selectedVersionId
                && !versions.some(version => version.id === this.selectedVersionId)
            ) {
                this.closeDetails()
            }
        },
    },
    methods: {
        formatDate(value) {
            return value ? this.$moment(value).format('YYYY-MM-DD HH:mm') : '—'
        },
        rowClass(item) {
            return item && item.id === this.selectedVersionId
                ? 'manifest-versions__row--selected'
                : ''
        },
        selectVersion(version) {
            if (!this.manifestId) {
                return
            }
            this.selectedVersionId = version.id
            this.showDefinition = false

            if (version.definition && version.validationReport) {
                this.$set(this.detailsById, version.id, version)
                return
            }
            if (this.detailsById[version.id]) {
                return
            }

            const requestId = ++this.requestId
            this.loadingVersionId = version.id
            ImportManifestApi.getVersion(this.manifestId, version.id)
                .then(response => {
                    if (requestId === this.requestId) {
                        this.$set(this.detailsById, version.id, response.data)
                    }
                })
                .catch(error => {
                    if (requestId === this.requestId) {
                        this.handleBackendError(error)
                    }
                })
                .finally(() => {
                    if (requestId === this.requestId) {
                        this.loadingVersionId = null
                    }
                })
        },
        closeDetails() {
            this.requestId += 1
            this.loadingVersionId = null
            this.selectedVersionId = null
            this.showDefinition = false
        },
        activateSelectedVersion() {
            if (!this.canActivateSelectedVersion) {
                return
            }

            this.activating = true
            ImportManifestApi.activateVersion(this.manifestId, this.selectedVersionId)
                .then(response => {
                    this.$emit('manifest-updated', response.data)
                    this.showDefaultSuccessMessage(
                        `Manifest revision ${this.selectedVersion.revision} has been activated.`
                    )
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.activating = false
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-versions__table ::v-deep tbody tr {
    cursor: pointer;
}

.manifest-versions__table ::v-deep .manifest-versions__row--selected {
    background: rgba(255, 159, 67, .08);
}

.manifest-version-detail {
    overflow: hidden;
    margin-top: 1.5rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.manifest-version-detail__header,
.manifest-version-detail__section,
.manifest-version-detail__loading {
    padding: 1.25rem;
}

.manifest-version-detail__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background: #f8f8f8;
}

.manifest-version-detail__loading {
    display: flex;
    align-items: center;
    color: #b9b9c3;
}

.manifest-version-detail__section + .manifest-version-detail__section {
    border-top: 1px solid #ebe9f1;
}

.manifest-version-detail__validation-groups {
    display: grid;
    gap: .75rem;
}

.manifest-version-detail__validation-group {
    padding: .75rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.manifest-version-detail__path {
    display: block;
    overflow-wrap: anywhere;
    margin-bottom: .75rem;
}

.manifest-version-detail__issue {
    display: flex;
    align-items: flex-start;
    gap: .75rem;
}

.manifest-version-detail__issue + .manifest-version-detail__issue {
    margin-top: .75rem;
}

.manifest-version-detail__json {
    overflow: auto;
    max-height: 32rem;
    margin-top: 1rem;
    padding: 1rem;
    border-radius: .357rem;
    color: #ebe9f1;
    background: #2f2d38;
    font-size: .8rem;
    white-space: pre;
}

@media (max-width: 767.98px) {
    .manifest-version-detail__header {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
